// Pfandarten: einfache (Flasche 0,08 €, Leerkasten 1,50 €) tragen datierte Beträge; Gebindepfänder (Kasten
// 20 × 0,5 l) bestehen aus Bestandteilen. Welches Artikel-Gebinde welche Pfandart trägt, pflegen die Stammdaten
// (Artikel → Einheiten) – hier steht es nur zum Lesen. Dazu der Leergutbestand je Standort.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { DEPOSIT_KINDS, zById, zDay, zDepositCents, zExpectedVersion, zLocationRef, zOptionalText, zPieceQty, zSaved, zText } from './common'

const READ = 'deposits.type.read'
const WRITE = 'deposits.type.write'

const zComponent = z.object({
  depositTypeId: zId,
  name: z.string(),
  /** Stück je Gebinde in Tausendstel (20 Flaschen = 20000) */
  qty: z.int(),
  /** Betrag je Stück am Stichtag; null = keiner hinterlegt */
  unitCents: z.int().nullable(),
})

export const zDepositTypeRow = z.object({
  id: zId,
  key: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  kind: z.enum(DEPOSIT_KINDS),
  /** Gebindepfand aus Bestandteilen (kein eigener Betrag) */
  isComposite: z.boolean(),
  /** der Betrag versteht sich brutto oder netto */
  amountBasis: z.enum(['gross', 'net']),
  /** Pfand je Stück heute in Cent (Gebinde: Summe der Bestandteile); null = kein Betrag hinterlegt */
  amountCents: z.int().nullable(),
  /** nächster, erst künftig gültiger Betrag einer einfachen Pfandart */
  upcoming: z.object({ amountCents: z.int(), validFrom: z.string() }).nullable(),
  components: z.array(zComponent),
  /** Leergut-Artikel, unter dem der Bestand geführt wird */
  emptiesItem: z.object({ id: zId, sku: z.string(), name: z.string() }).nullable(),
  /** Artikel-Gebinde, die diese Pfandart tragen */
  unitCount: z.int(),
  active: z.boolean(),
  version: z.int(),
})
export type DepositTypeRow = z.output<typeof zDepositTypeRow>

export const depositTypeList = defineContract({
  name: 'deposits.type.list',
  title: 'Pfandarten anzeigen',
  description: 'Listet die Pfandarten mit heutigem Betrag, Bestandteilen und der Zahl der Artikel-Gebinde, die sie tragen.',
  llm: {
    description:
      'Listet alle Pfandarten: einfache (Flasche, Leerkasten, Fass) mit ihrem Betrag in Cent und Gebindepfänder ' +
      '(Kasten 20 × 0,5 l) mit ihren Bestandteilen und der Summe. Verwenden, um die Pfandart für eine Leergutrücknahme ' +
      'oder -rückgabe zu finden. Welcher Artikel welches Pfand trägt, steht am Artikel (masterdata.item.get).',
    keywords: ['Pfand', 'Pfandart', 'Pfandbetrag', 'Leergut', 'Kastenpfand', 'Flaschenpfand', 'Einwegpfand'],
  },
  input: z.strictObject({ active: z.boolean().optional(), q: z.string().trim().max(120).optional() }),
  output: z.object({ rows: z.array(zDepositTypeRow) }),
  permission: READ,
  risk: 'read',
})

export const depositTypeGet = defineContract({
  name: 'deposits.type.get',
  title: 'Pfandart öffnen',
  description: 'Zeigt eine Pfandart mit allen Beträgen (Verlauf), Bestandteilen und den Artikeln, die sie tragen.',
  llm: {
    description:
      'Liefert eine Pfandart mit dem Verlauf ihrer Beträge (gültig ab), den Bestandteilen eines Gebindepfands und ' +
      'den Artikel-Gebinden, die diese Pfandart tragen (nur lesen – die Zuordnung pflegt der Artikel).',
    keywords: ['Pfandart öffnen', 'Pfandverlauf'],
  },
  input: zById,
  output: zDepositTypeRow.extend({
    taxMode: z.enum(['inherit', 'fixed']),
    looseReturnMax: z.int().nullable(),
    rates: z.array(z.object({ validFrom: z.string(), amountCents: z.int(), current: z.boolean() })),
    /** Gebindepfänder, in denen diese einfache Pfandart Bestandteil ist */
    usedIn: z.array(z.object({ id: zId, name: z.string(), qty: z.int() })),
    units: z.array(z.object({ itemUnitId: zId, itemId: zId, sku: z.string(), name: z.string(), unitLabel: z.string() })),
  }),
  permission: READ,
  risk: 'read',
})

const zComponentInput = z.strictObject({ depositTypeId: zId, qty: zPieceQty.describe('Stück je Gebinde in Tausendstel (20 Flaschen = 20000)') })

export const depositTypeCreate = defineContract({
  name: 'deposits.type.create',
  title: 'Pfandart anlegen',
  description: 'Legt eine einfache Pfandart mit Betrag oder ein Gebindepfand aus Bestandteilen an.',
  llm: {
    description:
      'Legt eine Pfandart an. Einfache Pfandart: `amountCents` (Pfand je Stück) und optional `emptiesItemId` (der ' +
      'Leergut-Artikel, unter dem der Bestand geführt wird). Gebindepfand: `components` (einfache Pfandarten mit ' +
      'Stück je Gebinde, z. B. 1 Leerkasten + 20 Flaschen) – ohne eigenen Betrag. Die Zuordnung zu Artikel-Gebinden ' +
      'geschieht danach am Artikel (Stammdaten).',
    keywords: ['Pfandart anlegen', 'neues Pfand', 'Gebindepfand'],
  },
  input: z.strictObject({
    key: z
      .string({ error: 'Bitte einen Schlüssel eingeben.' })
      .trim()
      .regex(/^[a-z][a-z0-9_]{1,39}$/, { error: 'Der Schlüssel besteht aus Kleinbuchstaben, Ziffern und Unterstrich (2 bis 40 Zeichen).' }),
    name: zText(80),
    shortName: zOptionalText(20),
    kind: z.enum(DEPOSIT_KINDS).default('reusable'),
    amountBasis: z.enum(['gross', 'net']).default('gross'),
    /** einfache Pfandart: Betrag je Stück ab heute */
    amountCents: zDepositCents.optional(),
    /** Gebindepfand: Bestandteile */
    components: z.array(zComponentInput).max(10).optional(),
    emptiesItemId: zId.nullish(),
  }),
  output: zSaved,
  permission: WRITE,
  risk: 'financial',
})

export const depositTypeUpdate = defineContract({
  name: 'deposits.type.update',
  title: 'Pfandart ändern',
  description: 'Ändert Name, Kurzname oder Leergut-Artikel einer Pfandart. Beträge ändert „Pfandbetrag ändern“.',
  llm: {
    description:
      'Ändert Name, Kurzname oder den Leergut-Artikel einer Pfandart – nur die mitgegebenen Felder; null leert. Den ' +
      'Betrag ändert deposits.type.setRate (mit Stichtag), die Bestandteile deposits.type.setComponents.',
    keywords: ['Pfandart umbenennen', 'Pfandart ändern'],
  },
  input: z.strictObject({ id: zId, expectedVersion: zExpectedVersion, name: zText(80).optional(), shortName: zOptionalText(20), emptiesItemId: zId.nullish() }),
  output: zSaved,
  permission: WRITE,
  risk: 'write',
})

export const depositTypeSetRate = defineContract({
  name: 'deposits.type.setRate',
  title: 'Pfandbetrag ändern',
  description: 'Setzt den Betrag einer einfachen Pfandart ab einem Stichtag. Bisherige Beträge bleiben im Verlauf.',
  llm: {
    description:
      'Setzt den Pfandbetrag einer EINFACHEN Pfandart in Cent ab `validFrom` (Standard heute, nie in der ' +
      'Vergangenheit). Der bisherige Betrag gilt bis zum Vortag weiter und bleibt im Verlauf; Gebindepfänder folgen ' +
      'automatisch (Summe ihrer Bestandteile). Ältere Belege und Kontobuchungen ändern sich nicht.',
    keywords: ['Pfandbetrag ändern', 'Pfand erhöhen', 'Pfanderhöhung', 'neuer Pfandsatz'],
  },
  input: z.strictObject({ id: zId, amountCents: zDepositCents, validFrom: zDay.optional() }),
  output: z.object({ id: zId, validFrom: z.string(), changed: z.boolean() }),
  permission: WRITE,
  risk: 'financial',
})

export const depositTypeSetComponents = defineContract({
  name: 'deposits.type.setComponents',
  title: 'Bestandteile eines Gebindepfands festlegen',
  description: 'Legt fest, aus welchen einfachen Pfandarten ein Gebindepfand besteht (Leerkasten + n Flaschen).',
  llm: {
    description:
      'Ersetzt die Bestandteile eines Gebindepfands: einfache Pfandarten mit Stück je Gebinde in Tausendstel ' +
      '(1 Leerkasten = 1000, 20 Flaschen = 20000). Wirkt auf künftige Belege; gebuchte Belege und Konten bleiben.',
    keywords: ['Bestandteile Pfand', 'Kastenpfand zusammensetzen'],
  },
  input: z.strictObject({ id: zId, components: z.array(zComponentInput).min(1, { error: 'Ein Gebindepfand braucht mindestens einen Bestandteil.' }).max(10) }),
  output: zSaved,
  permission: WRITE,
  risk: 'financial',
})

export const depositTypeDeactivate = defineContract({
  name: 'deposits.type.deactivate',
  title: 'Pfandart stilllegen',
  description: 'Nimmt eine Pfandart aus der Auswahl. Geht nur, wenn kein aktives Artikel-Gebinde sie mehr trägt.',
  llm: {
    description:
      'Legt eine Pfandart still: Sie steht bei Rücknahmen und am Artikel nicht mehr zur Auswahl. Nicht möglich, ' +
      'solange aktive Artikel-Gebinde oder ein aktives Gebindepfand sie verwenden. Konten und alte Belege bleiben.',
    keywords: ['Pfandart stilllegen', 'Pfandart löschen'],
  },
  input: zById,
  output: zSaved,
  permission: WRITE,
  risk: 'destructive',
  effects: { reversible: true },
})

export const depositTypeReactivate = defineContract({
  name: 'deposits.type.reactivate',
  title: 'Pfandart wieder aktivieren',
  description: 'Nimmt eine stillgelegte Pfandart wieder in die Auswahl.',
  llm: { description: 'Aktiviert eine stillgelegte Pfandart wieder.', keywords: ['Pfandart aktivieren'] },
  input: zById,
  output: zSaved,
  permission: WRITE,
  risk: 'write',
})

// ---- Leergutbestand ------------------------------------------------------------------------------------

export const emptiesStock = defineContract({
  name: 'deposits.stock.list',
  title: 'Leergutbestand anzeigen',
  description: 'Leergut je Pfandart und Standort – aus dem Bestand der Leergut-Artikel, bewertet zum Pfandbetrag.',
  llm: {
    description:
      'Liefert den Leergutbestand je einfacher Pfandart (Leerkästen, Flaschen, Fässer) an den Standorten, die die ' +
      'Person sehen darf: Stück je Standort (Tausendstel), Summe und Pfandwert zum heutigen Betrag. Verwenden für ' +
      '„wie viel Leergut steht in Garching“ oder vor einer Rückgabe an den Lieferanten.',
    keywords: ['Leergutbestand', 'Leergut im Lager', 'leere Kästen', 'Leerguthof', 'wie viel Leergut'],
  },
  input: z.strictObject({ locationId: zId.optional() }),
  output: z.object({
    locations: z.array(zLocationRef),
    rows: z.array(
      z.object({
        depositTypeId: zId,
        name: z.string(),
        shortName: z.string().nullable(),
        kind: z.enum(DEPOSIT_KINDS),
        itemId: zId,
        sku: z.string(),
        /** Pfand je Stück heute; null = kein Betrag hinterlegt */
        unitCents: z.int().nullable(),
        byLocation: z.array(z.object({ locationId: zId, qty: z.int() })),
        totalQty: z.int(),
        /** Pfandwert des Bestands (Stück × Betrag) */
        valueCents: z.int().nullable(),
      }),
    ),
    totalValueCents: z.int(),
  }),
  permission: 'deposits.stock.read',
  risk: 'read',
  scope: 'any',
})
